import React from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, TextField, InputAdornment, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { ProfileMenuButton } from '@/components/Auth/ProfileMenuButton';
import { useAuthStore } from '@/stores/authStore';

interface HeaderProps {
  title?: string;
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  showSearchBar?: boolean;
  onSearchClick?: () => void;
  onSendClick?: () => void;
  onInviteClick?: () => void;
}

/**
 * Top header component with logo, navigation, and user actions
 */
export const Header: React.FC<HeaderProps> = ({
  showMenuButton,
  onMenuClick,
  onSearchClick,
  onSendClick,
  onInviteClick,
}) => {
  const auth0User = useAuthStore((state) => state.auth0User);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'rgba(11, 14, 20, 0.9)',
        backdropFilter: 'blur(14px)',
        borderBottom: '1px solid rgba(167, 139, 250, 0.1)',
        color: 'text.primary',
        width: '100%',
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{ gap: 2, justifyContent: 'space-between' }}>
        {/* Left: Menu Button + Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {showMenuButton && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              onClick={onMenuClick}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="PigeonHole Logo"
              sx={{
                height: 32,
                width: 'auto',
                flexShrink: 0,
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                display: { xs: 'none', sm: 'block' },
                whiteSpace: 'nowrap',
              }}
            >
              [Beta] Nest Box
            </Typography>
          </Box>
        </Box>

        {/* Center: Search Bar - Flexible Width */}
        {auth0User && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', mx: 2, minWidth: 0 }}>
            <TextField
              placeholder="Search secrets..."
              onClick={onSearchClick}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                maxWidth: 500,
                cursor: 'pointer',
              }}
            />
          </Box>
        )}

        {/* Right: Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
          {auth0User && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onSendClick}
                size="small"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Send Secret
              </Button>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={onInviteClick}
                size="small"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                Invite
              </Button>
            </>
          )}

          {auth0User && (
            <>
              <IconButton
                color="inherit"
                onClick={onSendClick}
                sx={{ display: { xs: 'flex', sm: 'none' } }}
                title="Send Secret"
              >
                <SendIcon />
              </IconButton>

              <IconButton
                color="inherit"
                onClick={onInviteClick}
                sx={{ display: { xs: 'flex', sm: 'none' } }}
                title="Invite Recipients"
              >
                <PersonAddIcon />
              </IconButton>
            </>
          )}

          {/* <ThemeToggle /> */}

          {auth0User && <ProfileMenuButton />}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
